import axios from 'axios';
import { IAppReducer, IDetailStep } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  createSub,
  getDataByKey,
  getProductsByCompany,
} from 'shared/services/user-add-subscription';
import {
  Advantage,
  Coupon,
  Price,
  Product,
  VehicleData,
} from '../types/Add-subscription.types';
import {
  getPayment,
  getProperties,
  getSub,
} from '../utils/Add-subscription.util';

interface UseAddSubscriptionReturn {
  steps: IDetailStep[];
  navigate: ReturnType<typeof useNavigate>;
  products: Product[];
  vehicleData: VehicleData;
  loading: boolean;
  error: boolean;
  user: IAppReducer['authReducer'];
  period: 'Mensual' | 'Anual';
  counts: Record<number, number>;
  selectedAddons: Record<number, boolean>;
  isFormValid: boolean;
  formData: Record<string, Record<string, string>>;
  handleCount: (price: Price, increment: boolean) => void;
  handleAddonToggle: (priceId: number) => void;
  calculatePrice: (price: Price) => number;
  formatCurrency: (amount: number, currency: string) => string;
  getAdvantages: (product: Product, price: Price) => Advantage[];
  handlePurchase: () => void;
  handleFormValidChange: (isValid: boolean) => void;
  isAnyProductSelected: boolean;
  setPeriod: React.Dispatch<React.SetStateAction<'Mensual' | 'Anual'>>;
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>>>
  >;
  subtotal: number;
  isPurchasing: boolean;
  showAlertError: null | string;
  setShowAlertError: React.Dispatch<React.SetStateAction<null | string>>;
}

const STEPS: IDetailStep[] = [
  {
    description: 'ajustes',
    state: 'active',
    link: '/settings',
    permissionToNavigate: true,
  },
  {
    description: 'suscripciones',
    state: 'active',
    link: '/settings/subscriptions',
    permissionToNavigate: true,
  },
  {
    description: 'agregar',
    state: 'active',
    link: '/settings/subscriptions/add-subscription',
    permissionToNavigate: false,
  },
];

const initVehicleData = {
  autos: { '': [''] },
  motos: { '': [''] },
};

interface UseAddSubscriptionProps {
  appliedCoupon: Coupon | null;
  selectedPaymentMethod: string | null;
}

export const useAddSubscription = ({
  appliedCoupon,
  selectedPaymentMethod,
}: UseAddSubscriptionProps): UseAddSubscriptionReturn => {
  const navigate = useNavigate();
  const { authReducer: user } = useSelector((state: IAppReducer) => state);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [vehicleData, setVehicleData] = useState<VehicleData>(initVehicleData);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const [period, setPeriod] = useState<'Mensual' | 'Anual'>('Anual');
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [selectedAddons, setSelectedAddons] = useState<Record<number, boolean>>(
    {}
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<
    Record<string, Record<string, string>>
  >({});
  const [showAlertError, setShowAlertError] = useState<null | string>(null);

  useEffect(() => {
    const getProductsByCo = async () => {
      setLoading(true);
      setError(false);
      try {
        const products = await getProductsByCompany(user.companyId);
        setProducts(products);
        const vehicleDataResponse = await getDataByKey('brand_and_models');
        setVehicleData(vehicleDataResponse);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getProductsByCo();
  }, []);

  useEffect(() => {
    const defaultCounts = products
      .filter((product) => !product.is_addon)
      .reduce((acc, product) => {
        product.prices.forEach((price) => {
          if (price.periocity_id.name === period) {
            acc[price.id] = 0;
          }
        });
        return acc;
      }, {} as Record<number, number>);
    setCounts((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(defaultCounts)) {
        return defaultCounts;
      }
      return prev;
    });
  }, [products, period]);

  const handleCount = (price: Price, increment: boolean) => {
    setCounts((prev) => {
      const currentCount = prev[price.id] || 0;
      let newCount = increment ? currentCount + 1 : currentCount - 1;

      newCount = Math.max(0, Math.min(newCount, price.max_count));

      if (newCount !== currentCount) {
        return { ...prev, [price.id]: newCount };
      }
      return prev;
    });
  };

  const handleAddonToggle = useCallback((priceId: number) => {
    setSelectedAddons((prev) => ({
      ...prev,
      [priceId]: !prev[priceId],
    }));
  }, []);

  const calculatePrice = useCallback(
    (price: Price) => {
      const count = counts[price.id] || 0;
      let basePrice = price.price * count;

      if (count >= price.max_additional) {
        basePrice = price.additional_price * count;
      }

      return basePrice;
    },
    [counts, appliedCoupon]
  );

  const formatCurrency = (amount: number, currency: string): string => {
    const formatter = new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(amount);
  };

  const getAdvantages = (product: Product, price: Price): Advantage[] => {
    return price.advantages || product.advantages;
  };

  const handleFormValidChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handlePurchase = async () => {
    setIsPurchasing(true);
    try {
      const sub = getSub({
        customerAuthId: user.uid,
        agentAuthId: user.agentAuthData ? user.agentAuthData.uid : '',
        products,
        period: period,
        counts,
        appliedCoupon,
        selectedAddons,
      });
      const properties = getProperties({
        formData,
        products,
        period,
        selectedAddons,
      });
      const payment = getPayment({
        user,
        cardId: selectedPaymentMethod,
      });
      await createSub({
        properties,
        sub,
        authId: user.uid,
        payment,
      });
      navigate('/settings/subscriptions', {
        state: { showAlertSuccess: true },
        replace: true,
      });
    } catch (error) {
      console.error('Error creating subscription:', error);
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 500 &&
        error.response.data?.error?.message.toLowerCase() === 'cx not found'
      ) {
        setShowAlertError(
          'Error al crear la suscripción, cierre sesión e inicie nuevamente'
        );
      } else {
        setShowAlertError('Error al crear la suscripción');
      }
    } finally {
      setIsPurchasing(false);
    }
  };

  const isAnyProductSelected: boolean = Object.values(counts).some(
    (count) => count > 0
  );

  const subtotal = useMemo(() => {
    let baseTotal = products.reduce((total, product) => {
      if (!product.is_addon) {
        return (
          total +
          product.prices.reduce((productTotal, price) => {
            if (price.periocity_id.name === period) {
              return productTotal + calculatePrice(price);
            }
            return productTotal;
          }, 0)
        );
      }
      return total;
    }, 0);

    // Add the price of selected addons
    products.forEach((product) => {
      if (product.is_addon) {
        product.prices.forEach((price) => {
          if (selectedAddons[price.id] && price.periocity_id.name === period) {
            const relatedProductCount = products
              .filter((p) => !p.is_addon)
              .reduce((count, p) => {
                return (
                  count +
                  p.prices.reduce((pCount, pPrice) => {
                    if (
                      pPrice.product_type_id.name ===
                        price.product_type_id.name &&
                      pPrice.periocity_id.name === period
                    ) {
                      return pCount + (counts[pPrice.id] || 0);
                    }
                    return pCount;
                  }, 0)
                );
              }, 0);
            baseTotal += price.price * relatedProductCount;
          }
        });
      }
    });

    return baseTotal;
  }, [products, selectedAddons, period, calculatePrice, counts]);

  return {
    steps: STEPS,
    navigate,
    products,
    vehicleData,
    loading,
    error,
    user,
    period,
    counts,
    selectedAddons,
    isFormValid,
    formData,
    handleCount,
    handleAddonToggle,
    calculatePrice,
    formatCurrency,
    getAdvantages,
    handlePurchase,
    handleFormValidChange,
    isAnyProductSelected,
    setPeriod,
    setFormData,
    subtotal,
    isPurchasing,
    showAlertError,
    setShowAlertError,
  };
};
